/*
 * Ecranul 2: catalogul de servicii.
 *
 * Filtrele stau in starea ecranului, nu in adresa. Motivul e practic: la fiecare
 * tastare s-ar schimba ruta, s-ar remonta ecranul si campul de cautare si-ar
 * pierde cursorul. Cine vrea sa trimita un link catre un filtru trimite link
 * catre serviciu, care are ruta lui.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArticleCard } from '../components/ArticleCard';
import { config } from '../api/config';
import * as shop from '../api/shop';
import { useCounts } from '../components/CountsContext';
import { useToasts } from '../components/ToastContext';
import { Pager } from '../components/Pager';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { pricingHeadline } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import type { CatalogItem } from '../api/types';
import { t } from '../lib/i18n';

/*
 * Cate articole se arata in capul catalogului.
 *
 * Trei, nu toate: ecranul e despre ce se poate CERE, iar articolele sunt o
 * scurtatura oferita, nu subiectul lui. Cand sunt mai multe, legatura de sub
 * ele duce la lista intreaga.
 */
const ARTICLE_PREVIEW = 3;

export function CatalogScreen() {
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);

  /* Cautarea pleaca dupa o pauza in tastare. Fara pauza, se trimite o cerere pe
     litera si raspunsurile se intorc in alta ordine decat au plecat - grila ar
     arata rezultatele pentru "ac" dupa cele pentru "acce". */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(typed.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const categories = useAsync((signal) => shop.categories({ signal }), []);

  /*
   * Articolele care se potrivesc cu acelasi cuvant.
   *
   * DE CE AICI: catalogul e locul in care oamenii cauta ce vor, iar jumatate
   * din ce vor nu e un serviciu de cerut, ci ceva ce pot face singuri. Cine
   * scria "parola" primea o lista goala de servicii si deschidea tichet, desi
   * articolul care il rezolva in cinci minute era scris de luni de zile.
   *
   * Prin `helpList`, nu prin `search`: aici omul RASFOIESTE, deci filtrul
   * trebuie sa fie cel al rasfoirii - acelasi LIKE ca al serviciilor de sub el.
   * `search` ar fi si scris in jurnal o cautare pe care omul n-a facut-o din
   * caseta, si ar fi adus a doua oara aceleasi servicii.
   *
   * Numai cand omul a scris ceva: pe catalogul nefiltrat, un teanc de articole
   * deasupra serviciilor ar muta ecranul de la "ce pot cere" la altceva.
   */
  const articles = useAsync(
    (signal) =>
      query === ''
        ? Promise.resolve(null)
        : shop.helpList({ q: query, per_page: ARTICLE_PREVIEW }, { signal }),
    [query],
  );
  const list = useAsync(
    (signal) =>
      shop.catalog(
        { q: query, category_id: categoryId, page, per_page: config.pageSize },
        { signal },
      ),
    [query, categoryId, page],
  );

  const filtered = query !== '' || categoryId !== '';

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Catalog')}</p>
          <h1 className="page-title">{t('Service catalog')}</h1>
          <p className="page-sub">{t('Choose a service, review what it involves, then add it to your cart.')}</p>
        </div>
      </header>

      <div className="toolbar">
        <div className="field field--grow">
          <label className="field__label" htmlFor="catalog-search">{t('Search')}</label>
          <input
            id="catalog-search"
            className="field__control"
            type="search"
            placeholder={t('Service name')}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="catalog-category">{t('Category')}</label>
          <select
            id="catalog-category"
            className="field__control"
            value={categoryId}
            /* Fara categorii se cauta liber. Un filtru care n-a putut fi incarcat
               nu are voie sa opreasca catalogul. */
            disabled={categories.status === 'error'}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('All categories')}</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={String(category.id)}>
                {category.name} ({category.count})
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="btn"
          disabled={!filtered && typed === ''}
          onClick={() => {
            setTyped('');
            setQuery('');
            setCategoryId('');
            setPage(1);
          }}
        >{t('Clear')}</button>
      </div>

      {/*
        * Articolele intai, cand exista.
        *
        * Daca omul se poate descurca singur, aia e cea mai buna varianta pentru
        * amandoi - si e o varianta pe care n-o alege daca o vede sub un teanc
        * de servicii.
        *
        * O cadere aici nu se arata deloc: catalogul e ecranul principal si el
        * merge mai departe. Un chenar de eroare pentru o scurtatura pe care
        * omul nici n-a cerut-o ar arata ca si cum s-ar fi stricat catalogul.
        */}
      {articles.status === 'ready' && articles.data && articles.data.items.length > 0 ? (
        <section className="selfserve__group">
          <h2 className="selfserve__title">{t('Solve it yourself')}</h2>
          <div className="grid">
            {articles.data.items.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
          {articles.data.total > articles.data.items.length ? (
            <p className="selfserve__more">
              <Link to="/help">{t('See all help articles')}</Link>
            </p>
          ) : null}
        </section>
      ) : null}

      {list.status === 'loading' ? <Loading label={t('Loading services…')} /> : null}
      {list.status === 'error' && list.error ? (
        <ErrorState error={list.error} onRetry={list.reload} />
      ) : null}

      {list.status === 'ready' && list.data ? (
        list.data.items.length === 0 ? (
          /*
            * Cand articolele au raspuns, "niciun serviciu" nu mai e un esec -
            * omul are deja ceva de citit deasupra. Textul vechi il trimitea sa
            * caute altceva, adica exact departe de raspunsul pe care tocmai
            * i l-am dat.
            */
          <EmptyState
            title={t('No services match')}
            text={
              articles.data && articles.data.items.length > 0
                ? t('Nothing in the catalog matches, but the articles above might help.')
                : filtered
                  ? t('Try a different search term, or clear the filters.')
                  : t('No services are published for your company yet.')
            }
          />
        ) : (
          <>
            <div className="grid">
              {list.data.items.map((item) => (
                <ServiceCard key={item.id} item={item} />
              ))}
            </div>
            <Pager
              total={list.data.total}
              page={list.data.page}
              perPage={list.data.per_page}
              unit={['service', 'services']}
              onPage={setPage}
            />
          </>
        )
      ) : null}
    </>
  );
}

function ServiceCard({ item }: { item: CatalogItem }) {
  const { push } = useToasts();
  const { refreshCart } = useCounts();
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      await shop.addToCart(item.id, 1, '');
      push(`${item.name} added to cart.`);
      refreshCart();
    } catch (cause) {
      push(cause instanceof Error ? cause.message : t('Could not add to cart.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card">
      <p className="card__eyebrow micro-label">{item.category?.name ?? t('Uncategorised')}</p>
      <h2 className="card__title">
        {/* Toata fisa e la un clic distanta de titlu; butoanele de jos raman
            pentru actiune, ca sa nu existe doua tinte care fac acelasi lucru. */}
        <Link to={`/catalog/${item.id}`}>{item.name}</Link>
      </h2>
      <p className="card__text">{item.short_description}</p>

      {/*
        * Numai faptele care au venit.
        *
        * Cand furnizorul a ascuns costul ori SLA-ul, api-ul nu trimite cheia,
        * iar aici nu se deseneaza randul. Nu "—", nu "No charge": un fapt
        * ascuns care lasa o urma pe ecran invita la intrebari, iar "No charge"
        * ar fi de-a dreptul o promisiune.
        */}
      {/* Cand n-a venit niciuna, nu ramane nici lista: un chenar gol cu linie
          deasupra arata a ceva care n-a apucat sa se incarce. */}
      {item.pricing || item.sla !== undefined ? (
      <dl className="card__facts">
        {item.pricing ? (
        <div>
          {/*
            * "Cost serviciu", nu "Cost": cifra e cat costa SERVICIUL, de obicei
            * pe luna, iar butonul de dedesubt scrie "Adauga in cos". Sub numele
            * vechi, cele doua se citeau impreuna ca pretul a ce pui in cos.
            * "Cost" ramane numele coloanei din cos si de pe cerere, unde chiar
            * inseamna cat costa linia aia.
            */}
          <dt className="micro-label">{t('Service cost')}</dt>
          {/* O singura linie, pe `kind`: cifra fixa, "Variable" sau "No charge".
              Un serviciu cu 18000/luna + 450/element nu mai poate arata "No
              charge" - vezi pricingHeadline. Desfasurarea completa e pe fisa. */}
          <dd className="num">{pricingHeadline(item.pricing)}</dd>
        </div>
        ) : null}
        {item.sla !== undefined ? (
        <div>
          {/*
            * Timpul de RASPUNS din contract, nu termenul in care primesti ce ai
            * cerut. "Termen: 15 minutes" pe un card cu buton de cos promitea al
            * doilea lucru.
            *
            * Se spune "de raspuns" pe fata: sablonul comercial are trei campuri
            * de SLA - raspuns, interventie, rezolvare - si api-ul il trimite pe
            * primul. "SLA" singur ar fi lasat cititorul sa aleaga.
            */}
          <dt className="micro-label">{t('Response SLA')}</dt>
          <dd>{item.sla ?? '—'}</dd>
        </div>
        ) : null}
      </dl>
      ) : null}

      <div className="card__actions">
        <Link className="btn btn--sm" to={`/catalog/${item.id}`}>{t('Details')}</Link>
        <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={add}>
          {busy ? t('Adding…') : t('Add to cart')}
        </button>
      </div>
    </article>
  );
}
